import { useEffect, useState } from "react";
import { setPage, toShortDateWithoutTime, useHub } from "../../../core/Global";
import Indicator from "../../../components/base/Indicator";
import Icon from "../../../components/base/Icon";
import Modal from "../../../components/base/Modal";
import { toastError, toastSuccess } from "../../../components/base/Toast";


// Rinvox's own list of paused accounts, and the call each one is owed.
//
// The plan document asks for a telephone call as a hold runs out -- "not an email, not a
// notification". The renewal service raises the task in the team channel a few days before the
// end; this is where the team sees who is paused, whose call is due, and marks it made with what
// the customer said. Root and Rinvox system admins only.
export default function PausedAccounts() {

    const hub = useHub()

    const [items, itemsSet] = useState<any[]>([])
    const [loading, loadingSet] = useState<boolean>(true)
    const [calling, callingSet] = useState<any>()
    const [note, noteSet] = useState<string>("")

    const load = () => {
        hub.Get("/accounts/pausedaccounts/", res => {
            loadingSet(false)
            if (res.success) itemsSet(res.data ?? [])
            else toastError(res.message ?? "Couldn't load paused accounts.")
        }, () => loadingSet(false))
    }

    useEffect(() => {
        setPage("Paused Accounts")
        load()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const due = items.filter(f => f.callDue).length

    return (
        <>
            <div className="card">
                <div className="card-header border-0 pt-6">
                    <div className="card-title flex-column">
                        <h3 className="fw-bolder m-0">Paused Accounts</h3>
                        <div className="fs-7 text-gray-600 mt-1">
                            Accounts on "Pause my account". Each is owed a phone call as its pause runs out.
                            {due > 0 && <span className="text-danger fw-bold"> {due} call{due === 1 ? "" : "s"} due.</span>}
                        </div>
                    </div>
                </div>
                <div className="card-body pt-2">
                    {
                        loading ?
                            <div className="py-20"><Indicator /></div>
                            : items.length === 0 ?
                                <div className="text-center text-gray-600 py-15">
                                    <Icon icon="pause" size={30} className="text-gray-400 mb-4" />
                                    <div>No account is paused right now.</div>
                                </div>
                                :
                                <div className="table-responsive">
                                    <table className="table align-middle table-row-dashed fs-6 gy-4">
                                        <thead>
                                            <tr className="text-start text-gray-500 fw-bold fs-7 text-uppercase">
                                                <th>Customer</th>
                                                <th>Plan</th>
                                                <th>Paused</th>
                                                <th>Ends</th>
                                                <th>Call</th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {
                                                items.map(h =>
                                                    <tr key={h.subscriptionId}>
                                                        <td>
                                                            <div className="fw-bold text-gray-900">{h.name}</div>
                                                            <div className="fs-7 text-gray-600">{h.phone} · {h.email}</div>
                                                        </td>
                                                        <td>{h.planName}</td>
                                                        <td>{toShortDateWithoutTime(h.holdStartedAt)}</td>
                                                        <td>{toShortDateWithoutTime(h.holdEndsAt)}</td>
                                                        <td>
                                                            {
                                                                h.calledAt ?
                                                                    <div>
                                                                        <span className="badge badge-light-success fw-bold">
                                                                            Called {toShortDateWithoutTime(h.calledAt)}
                                                                        </span>
                                                                        <div className="fs-7 text-gray-600 mt-1">
                                                                            {h.calledBy}{h.callNote ? ": " + h.callNote : ""}
                                                                        </div>
                                                                    </div>
                                                                    : h.callDue ?
                                                                        <span className="badge badge-light-danger fw-bold">Call due</span>
                                                                        :
                                                                        <span className="fs-7 text-gray-600">
                                                                            From {toShortDateWithoutTime(h.callDueAt)}
                                                                        </span>
                                                            }
                                                        </td>
                                                        <td className="text-end">
                                                            {
                                                                !h.calledAt &&
                                                                <button type="button" className="btn btn-sm btn-light-primary"
                                                                    onClick={e => {
                                                                        e.preventDefault();
                                                                        noteSet("");
                                                                        callingSet(h);
                                                                        window.openModal("modal-hold-called");
                                                                    }}>
                                                                    <Icon icon="phone" type="solid" size={11} className="me-2" />
                                                                    Mark called
                                                                </button>
                                                            }
                                                        </td>
                                                    </tr>
                                                )
                                            }
                                        </tbody>
                                    </table>
                                </div>
                    }
                </div>
            </div>

            <Modal id="modal-hold-called"
                title={"Called " + (calling?.name ?? "")}
                buttonText="Save"
                onConfirm={callback => {
                    hub.Post("/accounts/pausedaccounts/called", { id: calling?.subscriptionId, note }, res => {
                        if (res.success) { toastSuccess("Call recorded."); itemsSet(res.data ?? []) }
                        else toastError(res.message)
                        callback(!res.success)
                    }, () => callback(true))
                }}>
                <div className="fw-bold mb-2">What did they say?</div>
                <textarea className="form-control form-control-solid" rows={4} value={note}
                    placeholder="Coming back next month / leaving / no answer ..."
                    onChange={e => noteSet(e.target.value)} />
            </Modal>
        </>
    )
}
